import { FC, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormDateField from "../../../../../components/forms/FormDateField";
import FormNumericField from "../../../../../components/forms/FormNumericField";
import FormSectionTitle from "../../../../../components/forms/FormSectionTitle";
import FormSelect from "../../../../../components/forms/FormSelect";
import FormTextField from "../../../../../components/forms/FormTextField";
import { CarbonSnapFormData } from "../../../../../datasources/carbonsnap/models";
import { GetOptionsContext, GetOptionsTopic } from "../../../../../datasources/requests/common";
import { Option } from "../../../../../datasources/responses/common";
import { useServerOptions } from "../../../../../hooks/useOptions";

interface Props {
	form: UseFormReturn<CarbonSnapFormData>;
	isLoading?: boolean;
}

type SelectFieldName = "weightUnit" | "temperature" | "calculationMode";

const TripDataFields: FC<Props> = ({ form, isLoading }) => {
	const { t } = useTranslation("feature.carbonsnap");
	const { t: tCommon } = useTranslation("common");

	const { data: dataWeightUnits, isLoading: weightUnitsLoading } = useServerOptions(
		GetOptionsTopic.MeasureUnits,
		GetOptionsContext.Weight
	);

	const { data: dataTemperatures, isLoading: temperaturesLoading } = useServerOptions(
		GetOptionsTopic.RefrigerationTypes
	);

	const { data: dataTransportModes, isLoading: transportModesLoading } = useServerOptions(
		GetOptionsTopic.TransportModes
	);

	// define weight unit options based on server data
	const weightUnitOptions = useMemo(() => {
		return (dataWeightUnits?.options || []).map((unit) => ({
			id: unit.id,
			label: unit.label,
		}));
	}, [dataWeightUnits?.options]);

	// define temperature options based on server data
	const temperatureOptions = useMemo(() => {
		return (dataTemperatures?.options || []).map((temperature) => ({
			id: temperature.id,
			label: temperature.label,
		}));
	}, [dataTemperatures?.options]);

	// define transport mode options based on server data
	const transportModeOptions = useMemo(() => {
		return (dataTransportModes?.options || []).map((mode) => ({
			id: mode.id,
			label: mode.label,
		}));
	}, [dataTransportModes?.options]);

	// select the first server option when the current value is missing or not valid
	// avoids MUI out-of-range warnings on select components
	useEffect(() => {
		const ensureValidOption = (name: SelectFieldName, options: Option[]) => {
			if (!options.length) return;

			const current = form.getValues(name);
			if (!options.some((option) => option.id === current)) {
				form.setValue(name, Number(options[0].id));
			}
		};

		ensureValidOption("weightUnit", weightUnitOptions);
		ensureValidOption("temperature", temperatureOptions);
		ensureValidOption("calculationMode", transportModeOptions);
	}, [form, weightUnitOptions, temperatureOptions, transportModeOptions]);

	return (
		<>
			<FormSectionTitle title={t("trip/data/title")} noPaddingTop />

			<Grid size={{ xs: 12, md: 6 }}>
				<FormDateField
					name="departureDate"
					label={t("trip/data/departure date")}
					form={form}
					isLoading={isLoading}
					fullWidth
					size="small"
					required
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<FormNumericField
					name="numberOfTrips"
					label={t("trip/data/number of trips")}
					form={form}
					isLoading={isLoading}
					fullWidth
					minFractionDigits={0}
					maxFractionDigits={0}
					size="small"
					required
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<FormNumericField
					name="weight"
					label={t("trip/data/weight")}
					form={form}
					isLoading={isLoading}
					fullWidth
					minFractionDigits={0}
					maxFractionDigits={2}
					size="small"
					required
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<FormSelect
					name="weightUnit"
					label={t("trip/data/weight unit")}
					form={form}
					options={weightUnitOptions}
					isLoading={weightUnitsLoading}
					fullWidth
					size="small"
					rules={{ required: tCommon("this field is required") }}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<FormSelect
					name="temperature"
					label={t("trip/data/temperature")}
					form={form}
					options={temperatureOptions}
					isLoading={temperaturesLoading}
					fullWidth
					size="small"
					rules={{ required: tCommon("this field is required") }}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<FormSelect
					name="calculationMode"
					label={t("trip/data/calculation mode")}
					form={form}
					options={transportModeOptions}
					isLoading={transportModesLoading}
					fullWidth
					size="small"
					rules={{ required: tCommon("this field is required") }}
				/>
			</Grid>

			<Grid size={12}>
				<FormTextField
					name="tripCode"
					label={t("trip/data/trip code")}
					form={form}
					isLoading={isLoading}
					fullWidth
					size="small"
				/>
			</Grid>
		</>
	);
};

export default TripDataFields;
